<template>
    <SideDrawer
        @close="closeDrawer"
        labelled-by="Apply filters to job list"
        described-by="Apply filters to job list"
    >
        <template #header>
            <div :class="$style.filterHeader">
                <h2>Filters</h2>
            </div>
        </template>
        <template #content>
            <div :class="$style.filterContainer">
                <div>
                    <JobFilter
                        v-for="filter in enrichedFilters"
                        :filter="filter"
                        @optionClicked="handleOptionClick"
                    />
                </div>
                <div :class="$style.filterFooter">
                    <button
                        name="remove all filters"
                        @click="clearAllFilters"
                        :class="$style.clearFilterButton"
                    >
                        Clear
                    </button>
                    <Button :class="$style.applyFiltersButton" @clicked="setFilters">
                        <template #buttonText> Apply Filters</template>
                    </Button>
                </div>
            </div>
        </template>
    </SideDrawer>
</template>

<script>
import { Button } from '@applied/marmot'
import SideDrawer from '@/components/SideDrawer'
import businessLogic from '@/utils/businessLogic.js'
import JobFilter from './JobFilter/JobFilter.vue'

const FILTERS_DISPLAY_ORDER = [
    'JobFunctions',
    'RemotePreference',
    'Locations',
    'EmploymentTypes',
    'Seniorities',
]

export default {
    name: 'JobFilterDrawer',
    components: {
        SideDrawer,
        Button,
        JobFilter,
    },
    data() {
        return {
            isJobFunctionFilterVisible: false,
            enrichedFilters: this.setEnrichedFilters(),
        }
    },
    props: {
        filters: {
            type: Object,
            required: true,
        },
        appliedFilters: {
            type: Array,
            required: true,
        },
        focusedFilterName: {
            type: String,
            required: false,
        },
    },
    computed: {
        locationEnrichedFilters() {
            return this.enrichedFilters.find((filter) => filter.name === 'Locations')
        },
    },
    methods: {
        getFilterLabel(filterName) {
            const filterLabelDictionary = {
                JobFunctions: 'Function',
                Locations: 'Location',
                Seniorities: 'Seniority',
                RemotePreference: 'Remote Preference',
                EmploymentTypes: 'Contract',
            }

            return filterLabelDictionary[filterName] || ''
        },
        handleOptionClick(optionClickPayload) {
            const option = this.enrichedFilters
                .find((enrichedFilter) => enrichedFilter.name === optionClickPayload.type)
                .options.find(
                    (enrichedFilterOption) =>
                        enrichedFilterOption.id === optionClickPayload.optionId,
                )

            option.isChecked = !option.isChecked

            // Location is a special case. While there are no unit tests for this, I leave these comments to document the
            // business logic and as a guide for PPL-907 (writing unit tests) (each comment should be a test).
            //
            // If option is a country with admin areas
            // If option is becoming true, then turn all the admin areas into true
            // If option is becoming false, then turn all the admin areas into false
            const hasAdminAreaSubFilters =
                option.type === 'country' &&
                businessLogic.showAdminAreaFiltersUnderCountry(
                    this.$store.getters.structuredAvailableLocations[option.name].jobCount(),
                )

            if (hasAdminAreaSubFilters) {
                this.locationEnrichedFilters.options.forEach((locOption) => {
                    if (locOption.type === 'adminArea' && locOption.country === option.name) {
                        locOption.isChecked = option.isChecked
                    }
                })
            }
            // If option is an admin area
            // If option is becoming true, then turn the country into true
            // If option is becoming false AND there are no other admin areas that are true, then turn the country into false
            if (option.type === 'adminArea') {
                const correspondingCountryOption = this.locationEnrichedFilters.options.find(
                    (locOption) => {
                        return locOption.id === option.country && locOption.type === 'country'
                    },
                )

                if (option.isChecked) {
                    correspondingCountryOption.isChecked = true
                }

                if (!option.isChecked) {
                    const areOtherAdminAreasCheckedForThisCountry =
                        this.locationEnrichedFilters.options.some((locOption) => {
                            return (
                                locOption.type === 'adminArea' &&
                                locOption.country === option.country &&
                                locOption.isChecked === true
                            )
                        })

                    if (!areOtherAdminAreasCheckedForThisCountry) {
                        correspondingCountryOption.isChecked = false
                    }
                }
            }
        },
        formatFilterCount(filter) {
            if (this.enrichedFilters[filter].length > 0) {
                return `(${this.enrichedFilters[filter].length})`
            }
            return ''
        },
        closeDrawer() {
            this.$emit('closeDrawer')
        },
        setFilters() {
            const selectedFilters = this.enrichedFilters
                .filter((enrichedFilter) =>
                    enrichedFilter.options.some(
                        (enrichedFilterOption) => enrichedFilterOption.isChecked,
                    ),
                )
                .map((filterWithCheckedOption) => {
                    return {
                        name: filterWithCheckedOption.name,
                        options: filterWithCheckedOption.options.filter((opt) => opt.isChecked),
                    }
                })
            this.$emit('applyFilters', selectedFilters)

            this.closeDrawer()
        },
        clearAllFilters() {
            this.enrichedFilters.forEach((filter) => {
                filter.options.forEach((option) => {
                    option.isChecked = false
                })
            })
        },
        getOptionCheckedStatus(filterOption, filterName) {
            return (
                this.appliedFilters
                    .find((appliedFilter) => appliedFilter.name === filterName)
                    ?.options?.find(
                        (appliedFilterOption) => filterOption.id === appliedFilterOption.id,
                    )?.isChecked ?? false
            )
        },
        setEnrichedFilters() {
            const filters = this.filters

            return FILTERS_DISPLAY_ORDER.map((filterName) => {
                const filterOptions = filters[filterName]
                const enrichedOptions = filterOptions.map((filterOption) => {
                    return {
                        ...filterOption,
                        isChecked: this.getOptionCheckedStatus(filterOption, filterName),
                    }
                })

                return {
                    name: filterName,
                    label: this.getFilterLabel(filterName),
                    options: enrichedOptions,
                    isInitiallyExpanded: this.focusedFilterName
                        ? filterName === this.focusedFilterName
                        : enrichedOptions.filter((option) => option.isChecked).length > 0,
                }
            })
        },
    },
}
</script>

<style module src="./JobFilterDrawer.css" />
